import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, Compass, Heart, ArrowRight } from "lucide-react";
import { ABOUT } from "@/constants/testIds";

const values = [
  { icon: Compass, title: "Independent", body: "We don't own pharmacies. We don't favour suppliers. We work for you - and only you." },
  { icon: Target, title: "Outcome-led", body: "Every feature is measured against one question: did it move your bottom line?" },
  { icon: Heart, title: "Human-first", body: "Our help line is staffed by pharma veterans. Not chatbots. Not call centres." },
];

export default function About() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-emerald" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-12 gap-8 items-end"
          >
            <div className="lg:col-span-8">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">About</div>
              <h1 data-testid={ABOUT.pageTitle} className="text-[4.1rem] font-bold text-[#0A2540] tracking-tight leading-[1.04] mb-6">
                Built by pharma veterans for independent pharmacies.
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
                Cascade Plus exists because community pharmacies deserve purchasing support that is independent, practical, and clear about where the saving comes from.
              </p>
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 gap-3">
              {[ ["100%", "independent"], ["\u00a30", "joining fee"] ].map(([n, label]) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                  <div className="text-xl font-bold text-[#0A2540]">{n}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-900/5">
                <img
                  src="https://images.pexels.com/photos/9574344/pexels-photo-9574344.jpeg"
                  alt="Clean modern pharmacy interior"
                  className="w-full h-[480px] object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Our story</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0A2540] mb-6 leading-tight">Inside UK pharmacy.</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-4">
                Working with the biggest names in pharmaceutical wholesale, we saw the same pattern again and again: independent pharmacies losing meaningful margin because they didn't have the buying power &mdash; or the time &mdash; to chase the best deal on every line.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                So we built the service we wished existed. Independent. Free. Plugged into your PMR. Cascade Plus turns hours of phone calls into a single click &mdash; and adds &pound;12,000+ to your bottom line each year doing it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 bg-slate-50/60 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">What we stand for</div>
            <h2 className="text-4xl font-bold text-[#0A2540]">Three principles. Zero compromise.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white border border-slate-200 rounded-2xl p-8"
              >
                <div className="h-12 w-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
                  <v.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-[#0A2540] mb-3">{v.title}</h3>
                <p className="text-slate-600 leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NUMBERS STRIP */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 justify-items-center">
            {[
              ["100%", "Independent"],
              ["\u00a30", "Joining fee"],
              ["9-6", "UK human support"],
            ].map(([n, l]) => (
              <div key={l} className="w-full max-w-[286px] rounded-2xl border border-slate-200 p-8 bg-gradient-to-br from-white to-slate-50 text-center">
                <div className="text-5xl font-bold text-[#0A2540] tracking-tight mb-2">{n}</div>
                <div className="text-sm text-slate-500 uppercase tracking-wider">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0A2540] mb-4">Want to talk to a human?</h2>
          <p className="text-lg text-slate-600 mb-8">We'd love to walk you through how Cascade Plus could work for your pharmacy.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#0A2540] text-white px-7 py-4 rounded-full font-semibold hover:bg-[#0F365C] transition-all hover:gap-3 shadow-lg"
          >
            Start the conversation
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
